import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../../core/shared/object/service/observer.service';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../core/shared/widget/service/widget.factory';
import {
    buildCwCaseTypeDefaultCoverageValueTableWidget
} from '../../../../widget/cw-case-type-default.widget';
import {cwWidgetToolbarCreate} from '../cw-case-type-object-component/shared/cw-case-type-widget.util';

/**
 * One parsed key→value pair of a Vorbelegung's alias-keyed {@code fieldValuesJson}, rendered as a
 * readable Feld→Wert row. The label is the raw {@code cc.FieldType} ALIAS — deliberately NOT
 * beautified: the alias IS the contract (it is what the apply step resolves and what SMART Seed
 * transports, D11), so showing anything else would hide the value that actually matters.
 */
interface CwFieldValueRow {
    /** The cc.FieldType alias the value is keyed by. */
    alias: string;
    /** German display value (booleans translated, nested payloads shown as JSON). */
    value: string;
}

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: [CW-VP-DIM] Dedicated object component for cw.CwCaseTypeDefault (Vorbelegung). A proposal
 *   owns child rows (CwCaseTypeDefaultCoverageValue) plus an alias-keyed fieldValuesJson, so per the
 *   PO decision Q7 it gets its OWN object page instead of an inline row editor or a node view.
 *   Renders the Set-Kopf (data widget + edit toggle), the READ-ONLY Vorbelegungs-Vorschau of
 *   fieldValuesJson as a Feld→Wert list, and the Bausteine child table.
 */
@Component({
    selector: 'cw-case-type-default-object-component',
    templateUrl: './cw-case-type-default-object.component.html',
    styleUrls: ['./cw-case-type-default-object.component.scss'],
    standalone: false
})
export class CwCaseTypeDefaultObjectComponent extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /** Set-Kopf: read view of the proposal's base data. */
    defaultDataWidget: WidgetData;
    /** Set-Kopf: metadata-driven edit form (lazily built on the first "Bearbeiten" toggle). */
    defaultEditWidget: WidgetData;
    editDefault: boolean = false;

    /** Child rows: the per-Baustein values of this proposal. */
    coverageValueWidget: WidgetData;

    /** Parsed, readable Feld→Wert rows of {@code fieldValuesJson}; empty when nothing is pre-filled. */
    fieldValueRows: CwFieldValueRow[] = [];

    /** True when {@code fieldValuesJson} carries content that is not parseable as a JSON object. */
    fieldValuesInvalid: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    /**
     * Called once the CwCaseTypeDefault DTO has loaded. Sets the page title and (re)builds the
     * proposal-scoped widgets plus the fieldValuesJson preview. G2.5: never calls refreshObject().
     */
    onObjectChanged(): void {
        this.setPageTitle(`${this.dto.name} – Vorbelegung`);
        this.editDefault = false;
        this.defaultEditWidget = null;
        this.buildDefaultDataWidget();
        this.buildCoverageValueWidget();
        this.buildFieldValueRows();
    }

    /**
     * Relays an @Input objectIdentifier swap to the base (which re-reads the object and thereby
     * re-fires onObjectChanged) — the case when this component is embedded rather than routed.
     */
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    /** Releases the base's object subscriptions (observer/refresh) when the page/drawer closes. */
    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private get caseTypeDefaultId(): number {
        return this.objectIdentifier.objectId;
    }

    private buildDefaultDataWidget(): void {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeDefault base-data read widget (Vorbelegung-Stammdaten).
         */
        this.defaultDataWidget = WidgetFactory.createWidgetEntityData(
            'config.cw.CwCaseTypeDefault.widget.data',
            'Vorbelegung-Stammdaten',
            'cw.CwCaseTypeDefault',
            this.caseTypeDefaultId
        );
    }

    /**
     * Toggles the Set-Kopf between the read data widget and the metadata-driven object form
     * (mvs-crud-object via the object widget) — no hand-built Angular form.
     */
    toggleEditDefault(): void {
        this.editDefault = !this.editDefault;
        if (this.editDefault && !this.defaultEditWidget) {
            /**
             * AI: Status "in progress" | Type Widget | SubType Runtime
             * Reason: CwCaseTypeDefault editable object form (Vorbelegung-Stammdaten, edit toggle).
             */
            this.defaultEditWidget = WidgetFactory.createWidgetObject(
                'config.cw.CwCaseTypeDefault.widget.object.edit',
                'Vorbelegung bearbeiten',
                'cw.CwCaseTypeDefault',
                this.caseTypeDefaultId
            );
        }
    }

    private buildCoverageValueWidget(): void {
        this.coverageValueWidget = buildCwCaseTypeDefaultCoverageValueTableWidget(this.caseTypeDefaultId);
        // "Anlegen" pre-fills (and locks) the owning proposal on the create form.
        this.coverageValueWidget.functionCallbacks = cwWidgetToolbarCreate([
            {field: 'caseTypeDefaultDtoId', value: this.caseTypeDefaultId}
        ]);
    }

    /**
     * Parses the proposal's alias-keyed {@code fieldValuesJson} into readable Feld→Wert rows.
     *
     * DEFENSIVE by design: the JSON is a PO-signed exception to row normalization (SD-10) and the
     * database protects neither its shape nor its keys. Unparseable content therefore sets
     * {@link fieldValuesInvalid} (the template says so explicitly) instead of throwing or silently
     * rendering an empty preview — a broken proposal must be VISIBLY broken.
     */
    private buildFieldValueRows(): void {
        this.fieldValueRows = [];
        this.fieldValuesInvalid = false;

        const raw: string = this.dto?.['fieldValuesJson'];
        if (!raw || !raw.trim()) {
            return;
        }

        let parsed: any;
        try {
            parsed = JSON.parse(raw);
        } catch {
            this.fieldValuesInvalid = true;
            return;
        }

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            this.fieldValuesInvalid = true;
            return;
        }

        for (const alias of Object.keys(parsed)) {
            this.fieldValueRows.push({alias: alias, value: this.displayValue(parsed[alias])});
        }
    }

    /**
     * Maps a raw JSON value to a German, human-readable display string: booleans → Ja/Nein,
     * null/undefined → "—", nested objects/arrays → their JSON text (shown, never dropped),
     * everything else stringified as-is.
     */
    private displayValue(value: any): string {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'boolean') {
            return value ? 'Ja' : 'Nein';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }
}
